/**
 * @file ReferenceSelect.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Friday, 25th October 2019 1:48:40 pm
 * @copyright 2015 - 2019 SKAT LLC, Delive LLC
 * @flow
 */

import type { ID, WaterlineWhere, WaterlineQuery, iDataManager, iDataProvider } from 'web-panel-essentials/types'
import type { Props as BaseProps, State as BaseState } from './Autocomplete'
import type { CombinedValue, iWindowManager, iLogger, ButtonModel, iExposedView } from '../../../types'

import ReferenceView from '../../../views/ReferenceView'
import * as React from 'react'
import { FORMAT, CAST } from 'web-panel-essentials/misc'
import Portal from 'react-relative-portal'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons'
import cn from 'classnames'
import Autocomplete from './Autocomplete'
import { Inject } from '../../../serviceLocator'
import compact from 'lodash/compact'
import { genTestName, __ } from 'web-panel/globals'
import autoBind from 'react-autobind'
import Button from '../components/Button'
import E from 'web-panel-essentials/errors'

library.add(faEllipsisH)

export type Props = BaseProps & {
  onRecordsChange?: (records: Array<Object>) => void,
  actions?: Array<ButtonModel>,
  parentWindowId?: ID,
  inverseColors?: boolean,
  border?: boolean,
  children?: Array<React.Component<Button>>,
  dataManager?: iDataManager<mixed>,
}

export type State = BaseState & {
  inputDisabled?: boolean,
  referenceIcon: string,
  role: string
}

type RefSelectWindowOptions = {
  dataProvider: iDataProvider<any>,
  filter?: WaterlineQuery,
  dialogOptions?: Object,
  dataManager?: iDataManager<mixed>,
}

class ReferenceSelect<P: Props, S: State> extends Autocomplete<P, S> {
  @Inject windowManager : iWindowManager
  @Inject logger : iLogger

  activate: Function
  triggerChange: Function
  handleReset: Function

  constructor (props: P) {
    super(props)
    autoBind(this)
    this.state = {
      ...this.state,
      referenceIcon: 'ellipsis-h',
      role: 'reference'
    }

    this.activate = this._activate.bind(this)
    this.handleReset = this._handleReset.bind(this)
    this.triggerChange = this._triggerChange.bind(this)
  }

  triggerRecordsChange (records: Array<CombinedValue>) : void {
    this.props.onRecordsChange && this.props.onRecordsChange(records)
  }

  _renderValues () {
    // STUB: Implemented in MultiSelect
  }

  async handleFilterChange (filter:WaterlineQuery = { }) {
    if (!this.props.dataProvider) return
    const where : ?WaterlineWhere = filter.where
    if (!where) return

    if (isEqual(filter, this.props.filter)) return

    const items = await this.props.dataProvider.get(filter)
    if (items && items[0]) this.activate(items[0])
    else {
      await this.setState({ items: [] })
      this.triggerChange({
        value: '',
        displayValue: ''
      })
    }
  }

  async _activate (item: Object) : Promise<void> {
    await super._activate(item)
    this.triggerRecordsChange([item])
  }

  handleKeyPress () : void {

  }

  renderReferenceButton () : React.Node {
    const { referenceIcon } = this.state
    const { value } = this.props
    return (
      <div className={cn('field-button', { active: value })} onClick={this.handleOpenReference}>
        <FontAwesomeIcon className='icon' icon={referenceIcon} />
      </div>
    )
  }

  invokeReferenceSelect ({ dataProvider, dataManager, dialogOptions, filter }: RefSelectWindowOptions) : Promise<Array<Object>> {
    return new Promise(async (resolve, reject) => { // eslint-disable-line
      const id = Symbol('ref-view')
      const extraActions = (dialogOptions && dialogOptions.actions) ? dialogOptions.actions : []
      const dialogDefaults = {
        id,
        title: __('ITEMS'),
        label: __('SELECT_ITEM'),
        details: __('SELECT_ITEM'),
        icon: 'list',
        width: 800,
        expandable: true,
        buttons: [{
          name: 'cancel',
          label: __('CANCEL'),
          title: __('CANCEL'),
          action: async () => {
            this.windowManager.close(id)
            reject(E('E_CCAN-A', 'CANCELLED'))
          }
        },
        ...extraActions,
        {
          name: 'save',
          isPrimary: true,
          label: __('SELECT'),
          title: __('SELECT'),
          action: async (referenceView: iExposedView<Array<any>>) => {
            const selectedRecords = await referenceView.data()
            resolve(selectedRecords)
            this.windowManager.close(id)
          }
        }]
      }

      await this.windowManager.show({
        ...{ ...dialogDefaults, ...dialogOptions },
        view: (
          <ReferenceView
            filter={filter}
            dataProvider={dataProvider}
            dataManager={dataManager}
            onDoubleClick={({ record }) => {
              this.windowManager.close(id)
              resolve([record])
            }}
          />
        )
      })
      await this.windowManager.activate(id)
    })
  }

  async handleOpenReference () : Promise<void> {
    if (this.props.disabled || this.props.readOnly) return
    try {
      const [record] = await this.invokeReferenceSelect({
        dataProvider: this.props.dataProvider,
        dataManager: this.props.dataManager,
        filter: this.props.filter,
        dialogOptions: {
          parent: this.props.parentWindowId,
          actions: this.props.actions
        }
      })
      if (this.props.disabled || this.props.readOnly) return

      if (record) {
        const changes = {
          value: record[this.valueKey],
          displayValue: CAST.String(record[this.displayValueKey])
        }
        this.triggerChange(changes)
        this.triggerRecordsChange([record])
      } else {
        this.triggerChange({
          value: null,
          displayValue: ''
        })
        this.triggerRecordsChange([])
      }
    } catch (e) {
      this.logger.warn(e.message)
    }
  }

  _renderReset () : React.Node {
    const { displayValue } = this.props
    return (
      <FontAwesomeIcon
        title={__('RESET')}
        icon='backspace'
        className={cn('reset-icon', { active: Boolean(displayValue) })}
        onClick={this.handleReset}
      />
    )
  }

  render () : React.Node {
    const { items = [], focused, pending, role, inputDisabled } = this.state
    const { className, disabled, inverseColors, placeholder, invalid, displayValue, readOnly, testName, tabIndex, border, value } = this.props

    return (
      <div
        className={cn(className, 'field', 'field-autocomplete', 'ref-select', border === false ? 'border-none' : '', { focused, disabled, invalid, pending, 'inverse-colors': inverseColors })}
        ref={(ref) => (this.container = ref)}
        role={role}
        title={compact([placeholder, displayValue]).join(': ')}
        onClick={this.handleClick}
      >

        <Portal top={15} left={-2} onOutClick={this.handleBodyClick}>
          <div {...genTestName('dropdown-list', testName)} className={cn('field-dropdown', { hidden: !items.length })}>
            {items.map(this.renderItem)}
          </div>
        </Portal>

        <div className='field-data'>
          {this._renderValues()}
          <input
            {...genTestName(testName)}
            readOnly={readOnly || inputDisabled}
            disabled={disabled}
            tabIndex={tabIndex}
            ref={(input) => { this.input = input }}
            placeholder={placeholder}
            className={cn('field-input', items.length ? 'protect-from-shortcuts' : '', { resolved: !isNil(value) })}
            value={FORMAT.String(displayValue)}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onKeyPress={this.handleKeyPress}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
          />
          {this._renderReset()}
        </div>

        {this.renderReferenceButton()}
      </div>
    )
  }
}

export default ReferenceSelect
